<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { onClickOutside } from '@vueuse/core';
import moment from 'moment';
import { useDraftImageStore } from '@/dashboard/store/draftImageStore';

const props = defineProps<{
  comment: {
    id: number;
    text: string;
    sender_id: number;
    sender: {
      name: string;
      pro?: boolean;
    };
    created_at: string;
    read?: boolean;
  };
  canHighlight?: boolean;
  preview?: boolean;
}>();

const draftImageStore = useDraftImageStore();
const { commentMenuId } = storeToRefs(draftImageStore);

const messageRef = ref<HTMLElement | null>(null);
const menuButtonRef = ref<HTMLElement | null>(null);

const highlight = computed(() => {
  const hash = window.location.hash;
  if (hash) {
    const id = +hash.substring(1);
    if (id === props.comment.id) {
      return true;
    }
  }

  if (!props.comment.read && props.canHighlight) {
    return true;
  }

  return false;
});

function openMenu() {
  const scrollView = document.querySelector('.messenger__comments') as HTMLElement | null;
  const currentCommentMenuId = commentMenuId.value;

  if (messageRef.value && scrollView) {
    draftImageStore.setCommentMenu({
      top: currentCommentMenuId ? null : messageRef.value.offsetTop - scrollView.scrollTop,
      id: currentCommentMenuId ? null : props.comment.id,
      userId: currentCommentMenuId ? null : props.comment.sender_id,
    });
  }
}

onClickOutside(menuButtonRef, (event) => {
  if (props.comment.id === commentMenuId.value) {
    setTimeout(() => {
      draftImageStore.setCommentMenu({
        top: null,
        id: null,
        userId: null,
      });
    }, 0);
  }
});
</script>

<template>
  <div :id="`comment-${comment.id}`" ref="messageRef" class="comment group" :class="{ highlight: highlight }">
    <div class="mb-[12px] flex items-start justify-between max-tablet:mb-xs">
      <div class="relative flex max-w-[80%] flex-col items-start justify-start">
        <div class="comment__author flex w-full items-center py-[6px] text-black dark:text-white max-tablet:!text-[14px] max-tablet:!font-medium">
          <span class="overflow-hidden overflow-ellipsis">{{ comment.sender.name }}</span>
          <svg
            v-if="comment.sender.pro"
            width="20"
            height="11"
            viewBox="0 0 20 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="ml-xxs min-w-[20px]"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0.306796 1.53692C0 2.13904 0 2.92726 0 4.5037V6.4963C0 8.07274 0 8.86096 0.306796 9.46308C0.576662 9.99273 1.00727 10.4233 1.53692 10.6932C2.13904 11 2.92726 11 4.5037 11H15.4963C17.0727 11 17.861 11 18.4631 10.6932C18.9927 10.4233 19.4233 9.99273 19.6932 9.46308C20 8.86096 20 8.07274 20 6.4963V4.5037C20 2.92726 20 2.13904 19.6932 1.53692C19.4233 1.00727 18.9927 0.576662 18.4631 0.306796C17.861 0 17.0727 0 15.4963 0H4.5037C2.92726 0 2.13904 0 1.53692 0.306796C1.00727 0.576662 0.576662 1.00727 0.306796 1.53692ZM2.22245 8.82128V2.06247H4.75C5.3935 2.06247 5.88441 2.23354 6.22274 2.57568C6.56108 2.91781 6.73024 3.39551 6.73024 4.00878C6.73024 4.62204 6.56108 5.09974 6.22274 5.44187C5.88441 5.78401 5.3935 5.95508 4.75 5.95508H3.05833V8.82128H2.22245ZM3.05833 5.24821H4.7301C5.0817 5.24821 5.35369 5.16429 5.54608 4.99645C5.7451 4.82216 5.84461 4.57685 5.84461 4.26054V3.75702C5.84461 3.4407 5.7451 3.19862 5.54608 3.03078C5.35369 2.85649 5.0817 2.76934 4.7301 2.76934H3.05833V5.24821ZM7.66175 8.16283H9.16435V4.48325H7.66175V3.8248H9.96043V5.0836H10.0102C10.1163 4.68982 10.3153 4.38319 10.6072 4.16371C10.9058 3.93777 11.2839 3.8248 11.7417 3.8248H12.4581V4.59945H11.4929C11.0351 4.59945 10.6636 4.72855 10.3784 4.98677C10.0997 5.24499 9.96043 5.58389 9.96043 6.00349V8.16283H11.9506V8.82128H7.66175V8.16283ZM14.4743 8.75349C14.7662 8.87615 15.0912 8.93747 15.4495 8.93747C15.8077 8.93747 16.1295 8.87615 16.4147 8.75349C16.7066 8.63084 16.9521 8.45655 17.1511 8.23061C17.3567 7.99821 17.5126 7.72386 17.6188 7.40754C17.7249 7.08477 17.778 6.72327 17.778 6.32304C17.778 5.92926 17.7249 5.57098 17.6188 5.24821C17.5126 4.92544 17.3567 4.65109 17.1511 4.42515C16.9521 4.19276 16.7066 4.01523 16.4147 3.89258C16.1295 3.76993 15.8077 3.7086 15.4495 3.7086C15.0912 3.7086 14.7662 3.76993 14.4743 3.89258C14.189 4.01523 13.9436 4.19276 13.7379 4.42515C13.5389 4.65109 13.3863 4.92544 13.2802 5.24821C13.174 5.57098 13.1209 5.92926 13.1209 6.32304C13.1209 6.72327 13.174 7.08477 13.2802 7.40754C13.3863 7.72386 13.5389 7.99821 13.7379 8.23061C13.9436 8.45655 14.189 8.63084 14.4743 8.75349ZM16.5242 7.88202C16.2522 8.14023 15.894 8.26934 15.4495 8.26934C15.005 8.26934 14.6468 8.14023 14.3748 7.88202C14.1028 7.6238 13.9668 7.22356 13.9668 6.68131V5.96476C13.9668 5.42251 14.1028 5.02227 14.3748 4.76406C14.6468 4.50584 15.005 4.37673 15.4495 4.37673C15.894 4.37673 16.2522 4.50584 16.5242 4.76406C16.7962 5.02227 16.9322 5.42251 16.9322 5.96476V6.68131C16.9322 7.22356 16.7962 7.6238 16.5242 7.88202Z"
              fill="#1C6AFF"
            />
          </svg>
        </div>
        <div class="comment__date text-gray-450 dark:text-gray-600 max-tablet:!text-[14px] max-tablet:!font-medium">
          {{
            moment(comment.created_at).calendar(null, {
              sameElse: 'll',
              lastWeek: 'dddd, h:mm a',
            })
          }}
        </div>
      </div>

      <div class="relative">
        <button
          ref="menuButtonRef"
          class="tppr-button__animation flex h-[24px] w-[24px] items-center justify-center rounded-4 stroke-gray-900 opacity-0 hover:bg-gray-250 active:bg-gray-150 group-hover:opacity-100 dark:stroke-gray-500 dark:hover:bg-gray-700 dark:hover:stroke-white dark:active:bg-gray-800 max-tablet:mt-[2px] max-tablet:opacity-100 laptop:mr-[-4px]"
          :class="{ 'bg-gray-100 opacity-100 dark:bg-gray-800': commentMenuId === comment.id }"
          @click="openMenu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="6" viewBox="0 0 20 6" fill="none">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M3 5C4.10457 5 5 4.10457 5 3C5 1.89543 4.10457 1 3 1C1.89543 1 1 1.89543 1 3C1 4.10457 1.89543 5 3 5Z"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10 5C11.1046 5 12 4.10457 12 3C12 1.89543 11.1046 1 10 1C8.89543 1 8 1.89543 8 3C8 4.10457 8.89543 5 10 5Z"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M17 5C18.1046 5 19 4.10457 19 3C19 1.89543 18.1046 1 17 1C15.8954 1 15 1.89543 15 3C15 4.10457 15.8954 5 17 5Z"
            />
          </svg>
        </button>
      </div>
    </div>
    <div
      v-linkify:options="{ target: '_blank', className: '' }"
      class="comment__text text-[15px] text-gray-900 dark:text-white max-tablet:text-[17px]"
      :class="{ preview: preview }"
    >
      {{ comment.text }}
    </div>
  </div>
</template>

<style lang="scss">
.dark .comment {
  &:after {
    background: repeating-linear-gradient(90deg, #424242 0 2px, transparent 0 4px);
  }

  &.highlight:before {
    background: #293886;
  }
}

.comment {
  position: relative;

  &__author {
    text-overflow: ellipsis;
    overflow: hidden;
    //max-width: 85px;
  }

  &__author,
  &__date {
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    white-space: nowrap;
    cursor: text;
  }

  &__text {
    font-style: normal;
    font-weight: 500;
    line-height: 166%;
    white-space: pre-wrap;
    word-break: break-word;
    cursor: text;

    a {
      color: #4489ff;
    }

    &.preview {
      -webkit-line-clamp: 18;
      -webkit-box-orient: vertical;
      display: -webkit-box;
      overflow: hidden;
    }
  }

  &:after {
    content: '';
    display: block;
    height: 1px;
    background: repeating-linear-gradient(90deg, #e3e3e3 0 2px, transparent 0 4px);
    margin: 11px -8px;
    margin-bottom: 7px;
  }

  &:last-child:after {
    content: none;
  }

  &:last-child:before {
    bottom: -4px;
  }

  &.highlight {
    &:before {
      background: #fcf6f0;
    }
  }

  &:before {
    content: '';
    border-radius: 4px;
    position: absolute;
    top: 0px;
    left: -8px;
    right: -8px;
    bottom: 8px;
    z-index: -1;
    transition: background 0.5s linear;
  }
}
</style>
